import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { isValidationError } from "@justhome/api-runtime";
import { QueryOperation } from "./gatewayComponents";

export type GatewayContext<TError = unknown> = {
  fetcherOptions: {
    headers?: {};
    queryParams?: {};
    next?: {
      tags?: string[];
      revalidate?: number | false;
    };
  };
  queryOptions: {
    enabled?: boolean;
    retry?: (failureCount: number, error: TError) => boolean;
  };
  queryKeyFn: (operation: QueryOperation) => QueryKey;
};

export function useGatewayContext<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  _queryOptions?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryKey" | "queryFn"
  >,
): GatewayContext<TError> {
  return {
    fetcherOptions: {},
    queryOptions: {
      retry: (failureCount: number, error: TError) => {
        if (isValidationError(error) && error?.cause?.status === 422)
          return false;

        return failureCount < 3;
      },
    },
    queryKeyFn,
  };
}

export const queryKeyFn = (operation: QueryOperation) => {
  const queryKey: unknown[] = hasPathParams(operation)
    ? operation.path
        .split("/")
        .filter(Boolean)
        .map((i) => resolvePathParam(i, operation.variables.pathParams))
    : operation.path.split("/").filter(Boolean);

  if (hasQueryParams(operation)) {
    queryKey.push(operation.variables.queryParams);
  }

  if (hasBody(operation)) {
    queryKey.push(operation.variables.body);
  }

  return queryKey;
};

const resolvePathParam = (key: string, pathParams: Record<string, string>) => {
  if (key.startsWith("{") && key.endsWith("}")) {
    return pathParams[key.slice(1, -1)];
  }
  return key;
};

const hasPathParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { pathParams: Record<string, string> };
} => {
  return Boolean((operation.variables as any).pathParams);
};

const hasBody = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { body: Record<string, unknown> };
} => {
  return Boolean((operation.variables as any).body);
};

const hasQueryParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { queryParams: Record<string, unknown> };
} => {
  return Boolean((operation.variables as any).queryParams);
};
